import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-integration-options-type-1';

/**
 * Section showcasing supported third-party integrations as a grid of icons.
 * Wrapped in a grey background card with a two-column inner layout: heading + body text on the left, icon grid on the right.
 * Differs from sibling integration-options variants by enclosing content in a tinted background card wrapper.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for integration options for {keyword}. 6-10 words.
 * @slot text - {content} <me-text> - Introductory paragraph listing integration categories. You MUST write 20-30 words.
 * @example
 *   <sn-integration-options-type-1
 *     icons='[{"src":"https://marketing-static.signnow.com/385/images/dynamic-prompt/salesforce.svg","alt":"Salesforce"}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Integration Options</me-heading>
 *     <me-text slot="text">With SignNow integrations for Microsoft applications.</me-text>
 *   </sn-integration-options-type-1>
 */
@Component({
  tag: 'sn-integration-options-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcIntegrationCards {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Integration icon items.
   * @type {array}
   * @item.type {object}
   * @item.property {string} src - Icon image URL
   * @item.property {string} alt - Alt text for the icon image
   * @item.required src
   * @item.required alt
   * @minItems 7
   * @maxItems 7
   */
  @Prop({ mutable: true }) icons?: { src?: string; alt?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `icons` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} text - Fallback body text (use slot="text" instead)
   * @property {array} icons - Fallback icon items (use icons prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        raw_html?: string;
        icons?: { src?: string; alt?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.icons = parseJsonProp(this.icons);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const icons = Array.isArray(this.icons)
      ? this.icons
      : (Array.isArray(values?.icons) ? values.icons : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__wrapper`}>
          <me-row direction="column" directionLg="row" yGapLg="none">
            <me-column spanLg="5">
              <div class={`${COMPONENT_NAME}__content`}>
                <slot name="heading"></slot>
                <slot name="text"></slot>
              </div>
            </me-column>
            {icons.length > 0 && (
              <me-column spanLg="7">
                <div class={`${COMPONENT_NAME}__grid`}>
                  {icons.map((icon) => (
                    <div class={`${COMPONENT_NAME}__icon-cell`}>
                      {icon.src && <me-picture class={`${COMPONENT_NAME}__icon`} src={icon.src} alt={icon.alt ?? ''} aspectRatio="auto" lazy={true}></me-picture>}
                    </div>
                  ))}
                </div>
              </me-column>
            )}
          </me-row>
        </div>
      </me-section>
    );
  }
}
